/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/user.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import { apiRequest } from "./config";
import {
  UserResponse,
  UserUpdate,
  TenantUserCreate,
  UserListResponse,
  TeamMembersResponse,
} from "@/types/user";

export const userApi = {
  /**
   * Get current user profile
   */
  async user_profile_get__includes_preferences(
    token: string,
  ): Promise<UserResponse> {
    return apiRequest<UserResponse>("/users/me", {
      method: "GET",
      token,
    });
  },

  /**
   * Update current user profile
   */
  async updateProfile(token: string, data: UserUpdate): Promise<UserResponse> {
    return apiRequest<UserResponse>("/users/me", {
      method: "PUT",
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Get team members for current manager's branch (ADR-004)
   * @see docs/02-design/18-adrs/ADR-team-members-endpoint.md
   */
  async getTeamMembers(
    token: string,
    includeInactive: boolean = false,
  ): Promise<TeamMembersResponse> {
    const params = new URLSearchParams();
    if (includeInactive) params.append("include_inactive", "true");
    const query = params.toString();
    return apiRequest<TeamMembersResponse>(`/team/members${query ? "?" + query : ""}`, {
      method: "GET",
      token,
    });
  },

  /**
   * Create new user (managers only).
   *
   * SPEC-0208 §2.3 (2026-06-25): Repointed from the deprecated POST /users/ (now HTTP 410 Gone)
   * to the canonical POST /api/v1/tenant-users/create, which anchors the new user to the
   * caller's tenant (tenant_id/tenant_key derived from the JWT). The canonical endpoint
   * expects the TenantUserCreate shape, which REQUIRES `username` (^[a-zA-Z0-9_-]+$, 3-100)
   * and a `role` that is a valid TenantUserRole (e.g. EMPLOYEE, BRANCH_MANAGER, WAITER…).
   * Callers must supply a TenantUserCreate-compatible payload.
   */
  async createUser(token: string, data: TenantUserCreate): Promise<UserResponse> {
    return apiRequest<UserResponse>("/tenant-users/create", {
      method: "POST",
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Get user by ID (managers only)
   */
  async getUserById(token: string, userId: string): Promise<UserResponse> {
    return apiRequest<UserResponse>(`/users/${userId}`, {
      method: "GET",
      token,
    });
  },

  /**
   * Get paginated user list (managers/admin only)
   */
  async getAllUsers(
    token: string,
    skip: number = 0,
    limit: number = 50,
    search?: string,
    role?: string,
  ): Promise<UserListResponse> {
    const params = new URLSearchParams();
    if (skip > 0) params.append("skip", skip.toString());
    if (limit !== 50) params.append("limit", limit.toString());
    if (search) params.append("search", search);
    if (role) params.append("role", role);
    
    const query = params.toString();
    return apiRequest<UserListResponse>(`/users/${query ? "?" + query : ""}`, {
      method: "GET",
      token,
    });
  },

  /**
   * Activate/deactivate user (admin only)
   */
  async toggleUserActivation(
    token: string,
    userId: string,
    isActive: boolean,
  ): Promise<UserResponse> {
    return apiRequest<UserResponse>(`/users/${userId}/activate`, {
      method: "POST",
      body: JSON.stringify({ is_active: isActive }),
      token,
    });
  },

  /**
   * Update user language preference
   */
  async updateLanguage(
    token: string,
    language: string,
  ): Promise<{ message: string }> {
    return apiRequest<{ message: string }>("/users/me/language", {
      method: "POST",
      body: JSON.stringify({ language }),
      token,
    });
  },

  /**
   * Get user statistics and performance metrics
   */
  async getUserStats(
    token: string,
    userId?: string,
    timeframe?: string,
  ): Promise<any> {
    const params = new URLSearchParams();
    if (userId) params.append("user_id", userId);
    if (timeframe) params.append("timeframe", timeframe);
    
    const query = params.toString();
    return apiRequest(`/users/stats${query ? "?" + query : ""}`, {
      method: "GET",
      token,
    });
  },

  /**
   * Update user profile with enhanced data
   */
  async updateProfileDetailed(
    token: string,
    data: UserUpdate,
  ): Promise<UserResponse> {
    return apiRequest<UserResponse>("/users/profile", {
      method: "PUT",
      body: JSON.stringify(data),
      token,
    });
  },
};
